import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function SkinIntegrityModal({ serverId, serviceUserId, onClose }) {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchRecords = async () => {
            setLoading(true);
            try {
                const ref = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'skinIntegrity');
                const snap = await getDocs(query(ref));
                
                const loaded = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                loaded.sort((a, b) => {
                    const statusA = a.status === 'resolved' || a.status === 'healed' ? 1 : 0;
                    const statusB = b.status === 'resolved' || b.status === 'healed' ? 1 : 0;
                    if (statusA !== statusB) return statusA - statusB;

                    const getMs = (val) => {
                        if (!val) return 0;
                        if (typeof val.toDate === 'function') return val.toDate().getTime();
                        if (val.seconds) return val.seconds * 1000;
                        return new Date(val).getTime() || 0;
                    };
                    const dateA = getMs(a.dateIdentified || a.createdAt || a.date);
                    const dateB = getMs(b.dateIdentified || b.createdAt || b.date);
                    return dateB - dateA;
                });
                
                setRecords(loaded);
            } catch (err) {
                console.error("Error fetching skin integrity:", err);
                setError("Failed to load skin integrity records.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId) {
            fetchRecords();
        }
    }, [serverId, serviceUserId]);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Skin Integrity</h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>Wound care, body maps, and pressure area monitoring</p>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading records...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : records.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🩺</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Skin Integrity Records</h3>
                            <p style={{ margin: 0, color: '#6b7280' }}>There are no skin integrity issues recorded for this service user.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {records.map((rec, index) => {
                                const issue = rec.issue || rec.issueType || 'Skin Assessment';
                                const location = rec.location || rec.bodyPart || 'Unspecified Location';
                                const isResolved = rec.status === 'resolved' || rec.status === 'healed';
                                
                                return (
                                    <div key={rec.id || index} style={{...styles.recordCard, opacity: isResolved ? 0.6 : 1}}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>{issue}</h3>
                                                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                                    Location: {location}
                                                </div>
                                            </div>
                                            <span style={{ 
                                                display: 'inline-block', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 'bold',
                                                backgroundColor: isResolved ? '#dcfce7' : '#fee2e2', 
                                                color: isResolved ? '#166534' : '#991b1b', 
                                                border: `1px solid ${isResolved ? '#bbf7d0' : '#fecaca'}`
                                            }}>
                                                {isResolved ? 'RESOLVED' : 'ACTIVE ISSUE'}
                                            </span>
                                        </div>
                                        
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Date Identified</label>
                                                <div style={styles.fieldValue}>{rec.dateIdentified || rec.createdAt || rec.date ? new Date((rec.dateIdentified || rec.createdAt || rec.date)?.seconds ? (rec.dateIdentified || rec.createdAt || rec.date).seconds * 1000 : (rec.dateIdentified || rec.createdAt || rec.date)).toLocaleDateString() : 'N/A'}</div>
                                            </div>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Severity / Grade</label>
                                                <div style={styles.fieldValue}>{rec.severity || rec.grade || 'Not specified'}</div>
                                            </div>
                                        </div>
                                        
                                        {rec.notes && (
                                            <div style={{ marginTop: '16px' }}>
                                                <label style={styles.fieldLabel}>Notes</label>
                                                <div style={{ fontSize: '13px', color: '#4b5563' }}>{rec.notes}</div>
                                            </div>
                                        )}
                                        
                                        <div style={{ marginTop: '16px' }}>
                                            <button style={styles.viewBtn} onClick={() => alert('View interactive Body Map: Coming to React V2 soon.')}>
                                                View Body Map
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    fieldLabel: { display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', marginBottom: '4px', fontWeight: 'bold' },
    fieldValue: { fontSize: '14px', color: '#111827' },
    viewBtn: { width: '100%', padding: '10px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }
};
